/**
 * IntentionFilters Component
 * Filter bar for intention detail page using shared popover components.
 */

import { X } from "lucide-react"
import { FilterChipButton } from "@/components/shared/FilterChipButton"
import { PriceFilterPopover } from "@/components/shared/PriceFilterPopover"
import { RadioPopover } from "@/components/shared/RadioPopover"
import { SingleToggleChipPopover } from "@/components/shared/SingleToggleChipPopover"
import type { IntentionFiltersState } from "../types"

interface IntentionFiltersProps {
  filters: IntentionFiltersState
  intentionName: string
  onChange: (filters: IntentionFiltersState) => void
}

const SORT_OPTIONS = [
  { value: "trending", label: "Trending" },
  { value: "newest", label: "Newest" },
]

const FORMAT_OPTIONS = [
  { value: "all", label: "All Formats" },
  { value: "IN_PERSON", label: "In-Person" },
  { value: "VIRTUAL_COURSE", label: "Live Virtual" },
  { value: "SELF_GUIDED", label: "Self-Guided" },
  { value: "SELF_GUIDED_COURSE", label: "Self-Guided Course" },
]

const DURATION_OPTIONS = [
  { value: "all", label: "All Durations" },
  { value: "short", label: "Short (under 30 min)" },
  { value: "medium", label: "Medium (30-60 min)" },
  { value: "long", label: "Long (over 60 min)" },
]

export function IntentionFilters({ filters, intentionName, onChange }: IntentionFiltersProps) {
  const hasActiveFilters =
    filters.sort !== "trending" ||
    filters.price !== "all" ||
    filters.format !== "all" ||
    filters.duration !== "all"

  const resetFilters = () => {
    onChange({ ...filters, sort: "trending", price: "all", format: "all", duration: "all" })
  }

  return (
    <div className="py-3 mt-1">
      <div className="flex items-center gap-[10px] flex-wrap">
        {/* Sort -- radio, immediate apply */}
        <RadioPopover
          label="Sort by"
          options={SORT_OPTIONS}
          value={filters.sort}
          onChange={v => onChange({ ...filters, sort: v as IntentionFiltersState["sort"] })}
        />

        {/* Price -- with slider matching Search page */}
        <PriceFilterPopover
          selectedPrice={filters.price !== "all" ? [filters.price] : []}
          priceRange={[0, 100]}
          onApply={price => {
            const filtered = price.filter(v => v !== "all")
            onChange({
              ...filters,
              price: (filtered.length > 0 ? filtered[0] : "all") as IntentionFiltersState["price"],
            })
          }}
        />

        {/* Format */}
        <SingleToggleChipPopover
          label="Format"
          options={FORMAT_OPTIONS}
          selected={filters.format}
          allValue="all"
          onApply={v =>
            onChange({ ...filters, format: (v ?? "all") as IntentionFiltersState["format"] })
          }
        />

        {/* Intention (read-only indicator) */}
        <FilterChipButton label="Intention:" value={intentionName} />

        {/* Duration */}
        <SingleToggleChipPopover
          label="Duration"
          options={DURATION_OPTIONS}
          selected={filters.duration}
          allValue="all"
          onApply={v =>
            onChange({ ...filters, duration: (v ?? "all") as IntentionFiltersState["duration"] })
          }
        />

        {/* Reset All */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className="flex items-center gap-[5px] pl-[10px] text-[14px] font-semibold text-brand-terracotta hover:text-brand-terracotta/80 transition-colors"
          >
            <span className="size-[20px] rounded-full border border-brand-terracotta flex items-center justify-center">
              <X className="size-[12px]" />
            </span>
            Reset All
          </button>
        )}
      </div>
    </div>
  )
}
